import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Eye } from 'lucide-react';
import { Payslip, Employee } from '@/types';
import { DetailView } from '@/components/ui/detail-view';
import { PayslipDetails } from '@/components/payslips/PayslipDetails';
import { formatCurrency } from '@/lib/utils';
import { useAppDataContext } from '@/contexts/AppDataContext';

interface RecentPayslipsProps {
  payslips: Payslip[];
}

export const RecentPayslips = ({ payslips }: RecentPayslipsProps) => {
  const [selectedPayslip, setSelectedPayslip] = useState<Payslip | null>(null);
  const [detailViewOpen, setDetailViewOpen] = useState(false);
  const { data } = useAppDataContext();
  const { employees } = data;
  
  const employeeMap = employees.reduce((map, emp) => {
    map[emp.id] = emp;
    return map;
  }, {} as Record<string, Employee>);
  
  const getEmployeeNameById = (employeeId: string) => {
    const employee = employeeMap[employeeId];
    return employee 
      ? `${employee.firstName} ${employee.lastName}` 
      : 'Unknown Employee';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'approved':
        return 'bg-blue-100 text-blue-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleViewDetails = (payslip: Payslip) => {
    setSelectedPayslip(payslip);
    setDetailViewOpen(true);
  };

  return (
    <Card className="col-span-2">
      <CardHeader>
        <CardTitle className="text-lg font-medium">Recent Payslips</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Employee</TableHead>
              <TableHead>Pay Period</TableHead>
              <TableHead>Net Salary</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payslips.length > 0 ? (
              payslips.map((payslip) => (
                <TableRow key={payslip.id}>
                  <TableCell className="font-medium">{getEmployeeNameById(payslip.employeeId)}</TableCell>
                  <TableCell>{payslip.payPeriod}</TableCell>
                  <TableCell>{formatCurrency(payslip.netSalary)}</TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(payslip.status)}>
                      {payslip.status.charAt(0).toUpperCase() + payslip.status.slice(1)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleViewDetails(payslip)}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-4">No payslips available</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>

      {selectedPayslip && (
        <DetailView 
          title={`Payslip - ${getEmployeeNameById(selectedPayslip.employeeId)} - ${selectedPayslip.payPeriod}`}
          isOpen={detailViewOpen}
          onClose={() => setDetailViewOpen(false)}
          size="xl"
        >
          <PayslipDetails 
            payslip={selectedPayslip}
            employeeName={getEmployeeNameById(selectedPayslip.employeeId)}
          />
        </DetailView>
      )}
    </Card>
  );
};
